import { CompoundButton, DefaultButton, IconButton, PrimaryButton, Stack } from "@fluentui/react";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import { DocumentOnePageSparkleRegular } from "@fluentui/react-icons";
import { useState } from "react";
import { logEvent } from "../../../../helper/AppInsightsHelper";
import { STYLES } from "../../../../helper/CRMEngagementAISummaryPanel";
import ADXLogo from "../../../../images/azure-data-explorer.png";
import { PromptLibraryItem } from "../../../../interfaces/slices.interfaces";
import CustomPanel from "../../../../SharedComponents/CustomPanel/CustomPanel";
import { useLogPromptLibraryClickActivityMutation } from "../../../../slices/AIHub.slice";
import { openQueryInADX } from "../../../../Utils/adx-utils";
import { KustoExplorerDialog } from "../Dialogs/KustoExplorerDialog";

const renderCommandButtonItems = (data: { description: string; query: string; prompt: string }) => {
  return data
    ? Object.entries(data).map(([key, value]) => {
        // Capitalize the first letter of the key
        const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
        return (
          <CompoundButton key={key} styles={STYLES.commandButton}>
            {data &&
              renderPromptLabelsWithDescription({
                text: capitalizedKey,
                description: value
              })}
          </CompoundButton>
        );
      })
    : null;
};

// Functions
function renderPromptLabelsWithDescription({ text, description }: { text: string; description: string }) {
  const descriptionItems = description ? (
    description.split(". ").map((item, index) => <p key={index}>{item}</p>)
  ) : (
    <p>N/A</p>
  );

  // Prepare plain text for clipboard
  const descriptionText = description ? description : "N/A";

  const handleCopy = () => {
    navigator.clipboard.writeText(descriptionText);
  };

  return (
    <span className={STYLES.textContainer}>
      <span className={STYLES.textContainerLabel}>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { width: "100%" } }}>
          <DocumentOnePageSparkleRegular style={STYLES.textContainerLabelIcon} />
          <span style={{ flex: 1 }}>{text}</span>
          <span style={{ marginLeft: "auto" }}>
            <IconButton iconProps={{ iconName: "Copy" }} title="Copy" ariaLabel="Copy" onClick={handleCopy} />
          </span>
        </Stack>
      </span>
      <div className={STYLES.textContainerDescription}>{descriptionItems}</div>
    </span>
  );
}

export const PromptCardButtonWithPanel = (props: PromptLibraryItem) => {
  const [logPromptLibraryClickActivity] = useLogPromptLibraryClickActivityMutation();
  const [enablePanel, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [showKustoDialog, setShowKustoDialog] = useState(false);

  const handleOpenInADX = (queryText: string) => {
    if (queryText.trim()) {
      setShowKustoDialog(true);
    }
    logEvent("Open in ADX link clicked", "AI Hub - KQL Mode", "PromptCardButtonWithPanel", { query: queryText });
  };

  const handleKustoDialogClose = () => {
    setShowKustoDialog(false);
  };

  const handleKustoRedirectComplete = () => {
    setShowKustoDialog(false);
    // Call openQueryInADX to actually open the Kusto link after dialog completes
    openQueryInADX(props.query ?? "");
  };

  // Centralized logging function with improved error handling and recovery
  const logActivity = async (eventType: "Execute" | "Open" | "Read"): Promise<boolean> => {
    try {
      const promptId = props.id?.toString();
      if (!promptId) {
        console.warn("Cannot log activity: promptId is missing");
        return false;
      }
      logEvent("Get started", "AI Hub - KQL Mode", "PromptCardButtonWithPanel", [
        { promptId: promptId, eventType: eventType, title: props.title }
      ]);
      await logPromptLibraryClickActivity({ promptId, eventType });
      return true;
    } catch (error) {
      console.error("Error logging user activity:", error);
      console.error(`Error logging ${eventType.toLowerCase()} activity:`, error);
      return false;
    }
  };

  // Separated concerns: this function only handles opening the panel
  const handleOpenPanel = () => {
    openPanel();
  };

  // Separate function for handling the complete panel open action (UI + logging)
  const handlePanelOpenWithLogging = async () => {
    handleOpenPanel();
    const logSuccess = await logActivity("Read");
    if (!logSuccess) {
      console.debug("Panel opened successfully, but logging failed");
    }
  };

  const handleRunClick = async (action: "run" | "open") => {
    dismissPanel();
    setTimeout(() => props.onRunPrompt?.(props, action), 200);
    const eventType: "Execute" | "Open" = action === "run" ? "Execute" : "Open";
    logActivity(eventType);
    const logSuccess = await logActivity(eventType);
    if (!logSuccess) {
      console.debug(`${action} action completed successfully, but logging failed`);
    }
  };

  const handleRenderIcon = () => <img src={ADXLogo} width={24} alt="Azure Data Explorer" />;

  return (
    <FluentProvider theme={webLightTheme}>
      <DefaultButton
        aria-roledescription="split button"
        iconProps={{ iconName: "CompanyDirectoryMirrored" }}
        primary
        split
        splitButtonAriaLabel="See other options"
        text="Get Started"
        menuProps={{
          items: [
            {
              key: "runPrompt",
              text: "Run",
              iconProps: { iconName: "Send" },
              onClick: () => {
                handleRunClick("run");
              }
            },
            {
              key: "exploreData",
              text: "Explore Data",
              iconProps: { iconName: "AzureDataExplorer" },
              onClick: () => {
                handleOpenInADX(props.query ?? "");
              },
              onRenderIcon: handleRenderIcon
            }
          ]
        }}
        onClick={handlePanelOpenWithLogging}
        styles={{
          root: {
            background: "var(--colorBrandBackgroundSelected)", // background color
            borderRadius: 10
          },
          rootHovered: {
            background: "var(--colorBrandBackgroundHover)" // hover background color
          },
          rootPressed: {
            background: "var(--colorBrandBackgroundPressed)" // pressed background color
          },
          splitButtonMenuButton: {
            background: "var(--colorBrandBackgroundSelected)", // menu button background
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
            "&:hover": { background: "var(--colorBrandBackgroundHover)" },
            "&:active": { background: "var(--colorBrandBackgroundPressed)" }
          }
        }}
      />
      <KustoExplorerDialog
        open={showKustoDialog}
        onClose={handleKustoDialogClose}
        onRedirectComplete={handleKustoRedirectComplete}
        query={props.query ?? ""}
      />
      <CustomPanel dismissPanel={dismissPanel} enablePanel={enablePanel} isLoading={false} panelTitle={props.title}>
        <Stack styles={STYLES.aiInsightsPanelContent}>
          {renderCommandButtonItems({
            /* Note: Keep the lines in the same order so the UI shows the prompt first and the query second. */
            description: props.description,
            prompt: props.prompt,
            query: props.query
          })}
          <PrimaryButton
            iconProps={{ iconName: "OpenInNewWindow" }}
            styles={{
              root: {
                background: "var(--colorBrandBackgroundSelected)", // background color
                borderRadius: 10,
                width: "fit-content"
              },
              rootHovered: {
                background: "var(--colorBrandBackgroundHover)" // hover background color
              },
              rootPressed: {
                background: "var(--colorBrandBackgroundPressed)" // pressed background color
              }
            }}
            onClick={() => handleRunClick("open")}
          >
            Open
          </PrimaryButton>
        </Stack>
      </CustomPanel>
    </FluentProvider>
  );
};
